import React, { useMemo } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { resolveAttachmentUrl } from '../../../utils/attachmentUrl';
import { copyToClipboard, shortenWalletAddress } from '../../../utils/commonFunction';
import { computeEarningsBreakdown } from '../utils/earningsMetrics';
import { computeCompanyDashboardMetrics } from '../utils/workspaceDashboardMetrics';
import { resolveTaskStatus } from '../helpers';

const InfoRow = ({ label, children }) => (
    <div className="flex flex-col gap-1 border-b border-zinc-100 py-3 last:border-0 dark:border-zinc-800">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
        <div className="text-sm text-zinc-900 dark:text-white">{children}</div>
    </div>
);

const StatTile = ({ label, value, hint }) => (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/40">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
        <p className="mt-1 text-xl font-bold tabular-nums text-zinc-900 dark:text-white">{value}</p>
        {hint ? <p className="mt-0.5 text-[11px] text-zinc-500">{hint}</p> : null}
    </div>
);

const JobsProfilePage = ({
    role = 'employee',
    walletAddress = '',
    company = null,
    employees = [],
    employeeCompany = null,
    employeeProfile = null,
    tasks = [],
    tokenSymbol = 'USDC',
    formatNumber,
    isCompanyOwner = false,
    onOpenEarnings,
    onOpenTasks,
    onEditCompany,
    onEditEmployeeProfile,
    onOpenEmployees,
    onOpenDisputed,
}) => {
    const isEmployee = role === 'employee';
    const isCompany = role === 'company';
    const breakdown = useMemo(
        () => computeEarningsBreakdown(tasks, role),
        [tasks, role],
    );
    const companyMetrics = useMemo(
        () => computeCompanyDashboardMetrics(tasks, employees),
        [tasks, employees],
    );
    const disputedCount = useMemo(
        () => tasks.filter((task) => resolveTaskStatus(task) === 'disputed').length,
        [tasks],
    );

    const formatAmt = (n) => `${formatNumber(n)} ${tokenSymbol}`;

    const profileImageUrl = isCompany
        ? company?.profileImageUrl
        : employeeProfile?.profileImageUrl;
    const resolvedAvatar = profileImageUrl
        ? resolveAttachmentUrl(profileImageUrl)
        : '';

    const displayName =
        employeeProfile?.name ||
        company?.name ||
        (walletAddress ? shortenWalletAddress(walletAddress) : 'Workspace member');

    const pageTitle = isCompany ? 'Company profile' : 'My profile';
    const pageSubtitle = isCompany
        ? 'Organization details, team overview, and payout snapshot in one place.'
        : 'Your wallet, company link, and earnings snapshot.';

    return (
        <div className="w-full space-y-6 pb-10">
            <div>
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
                    {pageTitle}
                </h1>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{pageSubtitle}</p>
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                <section className="space-y-5">
                    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                        <div className="flex items-start gap-4">
                            {walletAddress || resolvedAvatar ? (
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-zinc-100 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700">
                                    {resolvedAvatar ? (
                                        <img
                                            src={resolvedAvatar}
                                            alt=""
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <Jazzicon
                                            diameter={56}
                                            seed={jsNumberForAddress(walletAddress)}
                                        />
                                    )}
                                </div>
                            ) : null}
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                    {displayName}
                                </h2>
                                <p className="mt-0.5 text-xs capitalize text-zinc-500">
                                    {isEmployee ? 'Employee' : 'Company owner'}
                                </p>
                                {isCompany && isCompanyOwner ? (
                                    <span className="mt-2 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                                        Owner
                                    </span>
                                ) : null}
                                {employeeProfile?.status ? (
                                    <span className="mt-2 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                                        {employeeProfile.status}
                                    </span>
                                ) : null}
                            </div>
                        </div>

                        <div className="mt-5">
                            <InfoRow label="Wallet">
                                <span className="inline-flex flex-wrap items-center gap-2">
                                    <span className="font-mono text-[13px]">
                                        {walletAddress
                                            ? shortenWalletAddress(walletAddress)
                                            : 'Not connected'}
                                    </span>
                                    {walletAddress ? (
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(walletAddress)}
                                            className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
                                        >
                                            Copy
                                        </button>
                                    ) : null}
                                </span>
                            </InfoRow>

                            {isEmployee && employeeCompany ? (
                                <InfoRow label="Company">{employeeCompany.name}</InfoRow>
                            ) : null}

                            {isCompany && company ? (
                                <>
                                    <InfoRow label="Company name">{company.name}</InfoRow>
                                    <InfoRow label="Team size">
                                        {company.employeeSize || '—'}
                                    </InfoRow>
                                    {company.email ? (
                                        <InfoRow label="Email">{company.email}</InfoRow>
                                    ) : null}
                                    {company.website ? (
                                        <InfoRow label="Website">{company.website}</InfoRow>
                                    ) : null}
                                    {company.industry ? (
                                        <InfoRow label="Industry">{company.industry}</InfoRow>
                                    ) : null}
                                    {company.location ? (
                                        <InfoRow label="Location">{company.location}</InfoRow>
                                    ) : null}
                                    {company.description ? (
                                        <InfoRow label="About">{company.description}</InfoRow>
                                    ) : null}
                                    {company.ownerWallet ? (
                                        <InfoRow label="Owner wallet">
                                            <span className="font-mono text-[13px]">
                                                {shortenWalletAddress(company.ownerWallet)}
                                            </span>
                                        </InfoRow>
                                    ) : null}
                                </>
                            ) : null}

                            {employeeProfile?.hourlyRate != null &&
                            employeeProfile.hourlyRate !== '' ? (
                                <InfoRow label="Hourly rate">
                                    {`${formatAmt(employeeProfile.hourlyRate).replace(` ${tokenSymbol}`, '')} ${tokenSymbol}/hr`}
                                </InfoRow>
                            ) : null}

                            {employeeProfile?.jobTitle ? (
                                <InfoRow label="Job title">{employeeProfile.jobTitle}</InfoRow>
                            ) : null}
                            {employeeProfile?.department ? (
                                <InfoRow label="Department">{employeeProfile.department}</InfoRow>
                            ) : null}
                            {employeeProfile?.email ? (
                                <InfoRow label="Email">{employeeProfile.email}</InfoRow>
                            ) : null}
                            {employeeProfile?.phone ? (
                                <InfoRow label="Phone">{employeeProfile.phone}</InfoRow>
                            ) : null}
                            {employeeProfile?.bio ? (
                                <InfoRow label="Bio">{employeeProfile.bio}</InfoRow>
                            ) : null}
                        </div>

                        {isCompany && isCompanyOwner && typeof onEditCompany === 'function' ? (
                            <button
                                type="button"
                                onClick={onEditCompany}
                                className="mt-4 rounded-lg bg-teal-700 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                            >
                                Update company
                            </button>
                        ) : null}
                        {isEmployee &&
                        typeof onEditEmployeeProfile === 'function' ? (
                            <button
                                type="button"
                                onClick={onEditEmployeeProfile}
                                className="mt-4 rounded-lg bg-teal-700 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                            >
                                Edit my profile
                            </button>
                        ) : null}
                    </div>

                    {isCompany ? (
                        <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                Workspace overview
                            </h3>
                            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                <StatTile
                                    label="Employees"
                                    value={employees.length}
                                    hint={`${companyMetrics.activeEmployees} active`}
                                />
                                <StatTile
                                    label="Total tasks"
                                    value={companyMetrics.totalTasks}
                                />
                                <StatTile
                                    label="Active tasks"
                                    value={companyMetrics.activeTasks}
                                />
                                <StatTile
                                    label="Pending review"
                                    value={companyMetrics.pendingReview}
                                />
                                <StatTile
                                    label="Disputed"
                                    value={companyMetrics.disputedTasks}
                                    hint={
                                        companyMetrics.disputedTasks > 0
                                            ? 'Needs attention'
                                            : undefined
                                    }
                                />
                                <StatTile
                                    label="Completed"
                                    value={companyMetrics.completedTasks}
                                />
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {typeof onOpenEmployees === 'function' ? (
                                    <button
                                        type="button"
                                        onClick={onOpenEmployees}
                                        className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-semibold text-zinc-800 transition hover:border-teal-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-teal-700"
                                    >
                                        Manage employees
                                    </button>
                                ) : null}
                                {typeof onOpenDisputed === 'function' &&
                                companyMetrics.disputedTasks > 0 ? (
                                    <button
                                        type="button"
                                        onClick={onOpenDisputed}
                                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 transition hover:border-amber-300 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                                    >
                                        View disputed tasks
                                    </button>
                                ) : null}
                            </div>
                        </section>
                    ) : null}
                </section>

                <aside className="space-y-4">
                    <section className="rounded-2xl border border-zinc-200 bg-zinc-50/80 p-5 dark:border-zinc-800 dark:bg-zinc-900/40">
                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                            {isCompany ? 'Payout snapshot' : 'Earnings snapshot'}
                        </h3>
                        <p className="mt-3 text-2xl font-bold tabular-nums text-teal-700 dark:text-teal-400">
                            {formatAmt(breakdown.totalReleased)}
                        </p>
                        <p className="mt-1 text-xs text-zinc-500">
                            {isEmployee ? 'Total received' : 'Total paid out'}
                        </p>
                        <dl className="mt-4 space-y-2 text-xs">
                            <div className="flex justify-between gap-2">
                                <dt className="text-zinc-500">Awaiting release</dt>
                                <dd className="font-semibold tabular-nums text-zinc-800 dark:text-zinc-200">
                                    {formatAmt(breakdown.pendingRelease)}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-2">
                                <dt className="text-zinc-500">
                                    {isCompany ? 'Locked in escrow' : 'In escrow'}
                                </dt>
                                <dd className="font-semibold tabular-nums text-zinc-800 dark:text-zinc-200">
                                    {formatAmt(breakdown.inEscrow)}
                                </dd>
                            </div>
                        </dl>
                        {typeof onOpenEarnings === 'function' ? (
                            <button
                                type="button"
                                onClick={onOpenEarnings}
                                className="mt-4 w-full rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                            >
                                View full {isCompany ? 'payouts' : 'earnings'}
                            </button>
                        ) : null}
                    </section>

                    {typeof onOpenTasks === 'function' ? (
                        <button
                            type="button"
                            onClick={onOpenTasks}
                            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left text-sm font-semibold text-zinc-800 shadow-sm transition hover:border-teal-300 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:border-teal-700"
                        >
                            <i className="fa-regular fa-clipboard me-2 text-teal-600 dark:text-teal-400" />
                            {isEmployee ? 'Go to my tasks' : 'Go to created tasks'}
                        </button>
                    ) : null}

                    {typeof onOpenDisputed === 'function' ? (
                        <button
                            type="button"
                            onClick={onOpenDisputed}
                            className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left text-sm font-semibold text-zinc-800 shadow-sm transition hover:border-amber-300 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:border-amber-700"
                        >
                            <i className="fa-solid fa-triangle-exclamation me-2 text-amber-600 dark:text-amber-400" />
                            Disputed tasks
                            {disputedCount > 0 ? (
                                <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900 dark:bg-amber-500/20 dark:text-amber-200">
                                    {disputedCount}
                                </span>
                            ) : null}
                        </button>
                    ) : null}
                </aside>
            </div>
        </div>
    );
};

export default JobsProfilePage;
